/**
 * The infrastructure slice of the Admin surface: agent keys, network rules (D7)
 * and the go-live checklist. Phase 2.5b Part B; the checklist restored in U4.
 *
 * ⚠ `admin/go-live` WAS RETIRED (tax-groups §6, G2) AND IS BACK — owner ruling
 * 2026-08-14, *"I removed it and that was wrong"*. It returns the way it left:
 * route, screen, nav entry and data module in ONE change, because a nav entry
 * over a dead route (and a route no nav reaches) are the same defect from the
 * two sides. What does NOT return is `GET /admin/verify-flags` — the screen is
 * built on `GET /products`, which already carries the flag; see `GoLivePage`.
 *
 * Named `routes.infra.tsx` — not `routes.tsx` — for the same reason the data
 * module is `infra-api.ts`: the users/roles/avatars stream lands its screens
 * in this same folder, and the two streams must not race for one filename.
 * Each exports its own route array; `src/routes.extra.ts` (the seam file) is
 * where they meet.
 *
 * Both mount INSIDE `RequireCountry` + `AppShell` under
 * `/countries/:cc/admin/…`, so they can rely on `useCountryCode()` and the
 * resolved session. No provider layout is needed: these screens use inline
 * alerts, not the inquiries toast, so there is nothing that must survive
 * navigation between them.
 */

import type { RouteObject } from 'react-router-dom';
import { AgentKeysPage } from './AgentKeysPage';
import { CountrySettingsPage } from './CountrySettingsPage';
import { GoLivePage } from './GoLivePage';
import { HolidaysPage } from './HolidaysPage';
import { NetworkRulesPage } from './NetworkRulesPage';

export const adminInfraRoutes: RouteObject[] = [
  { path: 'admin/agent-keys', element: <AgentKeysPage /> },
  { path: 'admin/network-rules', element: <NetworkRulesPage /> },
  /*
   * COUNTRY SETTINGS — India go-live, cluster A3. The write surface for the letterhead
   * columns and the tax registration number; before it, `PATCH /countries/:cc` had no
   * user interface anywhere in the product and the one thing standing between India and
   * a printable quotation was unreachable from inside the product.
   *
   * It sits IMMEDIATELY BEFORE the checklist, in the array and in the nav, for the same
   * reason the checklist sits under Products: this is the screen where the checklist's
   * letterhead item is cleared. `admin-infra-nav.test.ts` pins that ordering, and pins
   * that this route is actually mounted — a nav row over a dead route and a screen no
   * nav reaches are the same defect from two sides.
   */
  { path: 'admin/country-settings', element: <CountrySettingsPage /> },
  { path: 'admin/go-live', element: <GoLivePage /> },
  /*
   * PUBLIC HOLIDAYS — the working calendar's supply route. It mounts with the
   * infrastructure slice because it IS country configuration, but it is DRAWN
   * under Field (see `NAV_DISPLAY_GROUPS`), beside the attendance register it
   * governs — the reader who needs it is looking at that register, not at a
   * settings list.
   */
  { path: 'admin/holidays', element: <HolidaysPage /> },
];
